/* [PATCH opal-k8 v1] Stran "Sinhronizacija" — cakalnica Opal sync (korak D).

   Uporablja IZKLJUCNO obstojece endpointe iz koraka C2:
     GET  /opal-cakalnica/{company_id}?status=&tip=
     POST /opal-cakalnica/{company_id}/{id}/potrdi
     POST /opal-cakalnica/{company_id}/{id}/prestavi
     POST /opal-cakalnica/{company_id}/{id}/povezi?partner_id=
     POST /opal-cakalnica/{company_id}/{id}/ustvari-loceno
     POST /opal-cakalnica/{company_id}/potrdi-vse

   En sam GET s praznim `status` vrne VSE postavke; odprte in zgodovino
   locimo na odjemalcu, da za prazno stanje (par.2.5) ze imamo stevilo
   razresenih brez drugega zahtevka.

   Seznami v opal_podatki/alfaberg_podatki so na strezniku oviti v
   {skupaj, prikazanih, vzorec} (_krajsaj v api/opal_cakalnica.py), zato
   jih tu odvijamo prek `seznam()`.
*/
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  RefreshCw, Loader, ChevronRight, ChevronDown, Check, X, AlertTriangle,
  Split, Link2, UserPlus, ScrollText, ChevronsUpDown, ChevronsDownUp,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { Zavihki } from '../components/Zavihki';
import { OpalVezava } from '../components/opal/OpalVezava';  // [PATCH multi-opal v1]
import { VascoVezava } from '../components/vasco/VascoVezava';   // [PATCH vasco-sync-fe v1]
// [PATCH vasco-cakalnica v1] cakalnica po programu podjetja (Opal nedotaknjen)
const cakPot = (prog: string | null | undefined) => (prog === 'vasco' ? '/vasco-cakalnica/' : '/opal-cakalnica/');
// [PATCH sinhronizacija-program-ime v1] besedila so pisana za Opal (privzeto); pri Vasco podjetju se ime programa
// zamenja s sklanjatvami. Opalovo vedenje se ne spremeni (niz vrne nespremenjen).
const _PROG_SKL: Record<string, string> = {
  Opal: 'Vasco', Opala: 'Vasca', Opalom: 'Vascom', Opalov: 'Vascov', Opalovi: 'Vascovi',
  Opalova: 'Vascova', Opalovo: 'Vascovo', Opalove: 'Vascove', Opalovih: 'Vascovih', Opalovega: 'Vascovega',
};
const zaProgram = (s: string, prog: string | null | undefined): string =>
  prog === 'vasco' ? s.replace(/Opal(?:ovega|ovih|ovi|ova|ovo|ove|ov|om|a)?\b/g, (m) => _PROG_SKL[m] ?? m) : s;

type Postavka = {
  id: number;
  sifrant: string;
  sifra: string;
  tip: string;
  status: string;
  opomba: string | null;
  razresil_user_id: string | null;
  ustvarjeno: string | null;
  razreseno: string | null;
  opal_podatki: any;
  alfaberg_podatki: any;
};

type Akcija = 'potrdi' | 'prestavi' | 'povezi' | 'ustvari-loceno';

const TIP_OZNAKA: Record<string, string> = {
  identiteta: 'razhajanje identitete',
  dvojnik_davcna: 'dvojnik po davčni',
  info_zdruzitev: 'verjetno združen',
};

const TIP_BARVA: Record<string, string> = {
  identiteta: 'bg-amber-100 dark:bg-amber-500/25 text-amber-700 dark:text-amber-300',
  dvojnik_davcna: 'bg-rose-100 dark:bg-rose-500/25 text-rose-700 dark:text-rose-300',
  info_zdruzitev: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
};

const STATUS_OZNAKA: Record<string, string> = {
  odprta: 'odprta',
  potrjena: 'potrjena',
  prestavljena: 'prestavljena',
  ustvarjena_loceno: 'ustvarjen ločen partner',
  samodejno_zaprta: 'samodejno zaprta',
};

/* Stavek, ki pove, KAJ se bo zgodilo (par.2.1). */
const AKCIJA_OPIS: Record<Akcija, string> = {
  'potrdi':
    'Davčna in naziv se prevzameta iz Opala; ostala polja uskladi naslednja '
    + 'sinhronizacija (do 5 min). Postavka gre v zgodovino.',
  'prestavi':
    'AlfaBergov partner na tej šifri dobi NOVO šifro po nastavitvi številčenja '
    + 'podjetja. Dokumenti in knjižbe ostanejo pri njem. Opalovega partnerja na '
    + 'sproščeni šifri ustvari naslednja sinhronizacija.',
  'povezi':
    'Izbrani obstoječi partner prevzame Opalovo šifro. Njegova polja uskladi '
    + 'naslednja sinhronizacija.',
  'ustvari-loceno':
    'Iz Opalovih podatkov nastane NOV partner na tej šifri, čeprav ima isto '
    + 'davčno kot obstoječi. Obstoječi partner ostane nespremenjen.',
};

const AKCIJA_NASLOV: Record<Akcija, string> = {
  'potrdi': 'Potrdi — isti subjekt',
  'prestavi': 'Prestavi — dva subjekta',
  'povezi': 'Poveži z obstoječim partnerjem',
  'ustvari-loceno': 'Ustvari ločenega partnerja',
};

const fmtCas = (v: string | null) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleString('sl-SI', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/* Streznik dolge sezname ovije v {skupaj, prikazanih, vzorec}. */
const seznam = (v: any): any[] => {
  if (Array.isArray(v)) return v;
  if (v && Array.isArray(v.vzorec)) return v.vzorec;
  return [];
};

const prazno = (v: any) => v === null || v === undefined || String(v).trim() === '';

/* [PATCH opal-k8 d2] Naziv partnerja za ZAPRTO vrstico (par.2.1):
   AlfaBergov naziv, sicer Opalov. Postavke tipa 'dvojnik_davcna' AB
   partnerja na tej sifri se NIMAJO (zato je alfaberg_podatki.name
   prazen) — takrat pove ime Opalov _naziv oz. prvi kandidat. */
const nazivPostavke = (p: Postavka): string => {
  const ab: any = p.alfaberg_podatki || {};
  const op: any = p.opal_podatki || {};
  const kand = seznam(ab.kandidati);
  return String(ab.name || ab.ab_naziv || (kand[0] && kand[0].name)
    || op.LongTitle || op.Naziv || op._naziv || '');
};

/* Primerjava vrednosti za poudarjanje razlik: brez presledkov in velikosti crk. */
const enako = (a: any, b: any) => {
  const n = (x: any) => String(x ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
  return n(a) === n(b);
};

/* Polja za primerjavo Opal <-> AlfaBerg (par.2.1). Opalove vrednosti so
   SUROVE iz ogledala (npr. KrajRac '1000  Ljubljana', DrzavaRac 'Slovenija');
   sinhronizacija jih ob prepisu razbije na kraj/posto in pretvori v ISO
   oznako, zato se tu lahko pokazeta kot razlicni, celo kadar bo rezultat
   enak. Na to opozarja opomba pod tabelo.

   DAVCNA ima lahko VEC vrstic. Izmerjeno 01.09.2026 na sifri S01059C:
   Opal ima v isti vrstici ogledala DVE razlicni davcni — DavcStev
   'NL8073432717B02' in TaxNumber 'NL8073423717B02' (prestavljeni cifri) —
   AlfaBerg pa se ujema z DavcStev. Razlog postavke izhaja iz ogledalovega
   stolpca `davcna` (= _davcna), ki sledi TaxNumber. Ce bi prikazali samo
   eno vrednost, bi primerjava kazala UJEMANJE, razlog nad njo pa razhajanje.
   Zato prikazemo vsako razlicico posebej. */
function primerjava(o: any, a: any) {
  const opal = o || {};
  const ab = a || {};
  const abDavcna = [ab.tax_number, ab.vat_number].filter((x) => !prazno(x)).join(' / ');

  // vrstice davcne: samo tiste, ki nosijo RAZLICNO informacijo
  const davcneVrstice: { polje: string; o: any; a: any }[] = [];
  const dodajDavcno = (polje: string, vrednost: any) => {
    if (prazno(vrednost)) return;
    if (davcneVrstice.some((v) => enako(v.o, vrednost))) return;
    davcneVrstice.push({ polje, o: vrednost, a: abDavcna });
  };
  dodajDavcno('Davčna (DavcStev)', opal.DavcStev);
  dodajDavcno('Davčna (TaxNumber)', opal.TaxNumber);
  // kljuc, po katerem se je sinhronizacija odlocila; prikazemo ga samo, kadar
  // ga v surovih vrednostih ni videti (sicer bi bila vrstica odvec)
  if (!prazno(opal._davcna)
      && !String(opal.DavcStev ?? '').includes(String(opal._davcna))
      && !String(opal.TaxNumber ?? '').includes(String(opal._davcna))) {
    davcneVrstice.push({
      polje: 'Davčna (ključ ujemanja)', o: opal._davcna, a: abDavcna,
    });
  }
  if (davcneVrstice.length === 0 && !prazno(abDavcna)) {
    davcneVrstice.push({ polje: 'Davčna', o: '', a: abDavcna });
  }
  if (davcneVrstice.length === 1) davcneVrstice[0].polje = 'Davčna';

  const vrstice = [
    { polje: 'Naziv', o: opal.LongTitle || opal.Naziv || opal._naziv, a: ab.name },
    ...davcneVrstice,
    { polje: 'Naslov', o: opal.NaslovRac, a: ab.address },
    { polje: 'Kraj', o: opal.KrajRac, a: ab.city },
    { polje: 'Pošta', o: opal.PostaRac, a: ab.postal_code },
    { polje: 'Država', o: opal.DrzavaRac, a: ab.country },
    { polje: 'Matična', o: opal.MaticnaSt, a: ab.registration_number },
    { polje: 'IBAN', o: opal.Racun, a: ab.iban },
  ];
  // samo polja, ki obstajajo na vsaj eni strani
  return vrstice.filter((v) => !prazno(v.o) || !prazno(v.a));
}

/* Polja, pri katerih je razlika lahko zgolj navidezna, ker Opalove vrednosti
   prikazujemo surove, sinhronizacija pa jih ob prepisu pretvori. */
const POLJA_PRETVORBA = ['Kraj', 'Pošta', 'Država'];

/* ------------------------------------------------------------------ */
/* Potrditveni dialog                                                  */
/* ------------------------------------------------------------------ */
function PotrditevDialog({ postavka, akcija, izvajam, onPotrdi, onZapri, izvozProgram }: {
  izvozProgram?: string | null;   // [PATCH sinhronizacija-program-ime v1]
  postavka: Postavka;
  akcija: Akcija;
  izvajam: boolean;
  onPotrdi: (partnerId?: string) => void;
  onZapri: () => void;
}) {
  const kandidati = seznam(postavka.alfaberg_podatki?.kandidati);
  const rabiIzbiro = akcija === 'povezi' && kandidati.length > 1;
  const [izbrani, setIzbrani] = useState<string>(
    kandidati.length === 1 ? String(kandidati[0]?.partner_id || '') : ''
  );
  const zapri = postavka.tip === 'info_zdruzitev' && akcija === 'potrdi';

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={izvajam ? undefined : onZapri} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="pointer-events-auto w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {zapri ? 'Zapri postavko' : AKCIJA_NASLOV[akcija]}
            </h3>
            <button onClick={onZapri} disabled={izvajam}
              className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-40">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-5 py-4 space-y-3">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Šifra <span className="font-mono font-medium text-gray-900 dark:text-white">{postavka.sifra}</span>
              {' · '}
              <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', TIP_BARVA[postavka.tip])}>
                {TIP_OZNAKA[postavka.tip] || postavka.tip}
              </span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {zapri
                ? 'Postavka se samo označi kot videna in zapre. V partnerja se NE piše nič.'
                : zaProgram(AKCIJA_OPIS[akcija], izvozProgram)}
            </p>

            {rabiIzbiro && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Izberi partnerja, ki prevzame šifro {postavka.sifra}:
                </p>
                <div className="max-h-56 overflow-y-auto space-y-1">
                  {kandidati.map((k: any) => (
                    <button key={String(k.partner_id)} type="button"
                      onClick={() => setIzbrani(String(k.partner_id))}
                      className={clsx(
                        'w-full text-left px-3 py-2 rounded-lg border text-sm transition',
                        izbrani === String(k.partner_id)
                          ? 'bg-blue-50 dark:bg-blue-950 border-blue-400'
                          : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 hover:border-blue-400')}>
                      <div className="font-medium text-gray-900 dark:text-white">{k.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        šifra <span className="font-mono">{k.vasco_code || '—'}</span>
                        {' · davčna '}
                        <span className="font-mono">
                          {[k.tax_number, k.vat_number].filter((x: any) => !prazno(x)).join(' / ') || '—'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-800">
            <button onClick={onZapri} disabled={izvajam}
              className="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40">
              Prekliči
            </button>
            <button
              onClick={() => onPotrdi(akcija === 'povezi' ? (izbrani || undefined) : undefined)}
              disabled={izvajam || (rabiIzbiro && !izbrani)}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
              {izvajam && <Loader className="w-4 h-4 animate-spin" />}
              {zapri ? 'Zapri' : 'Izvedi'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Dialog za "Potrdi vse"                                              */
/* ------------------------------------------------------------------ */
function PotrdiVseDialog({ stevilo, izvajam, onPotrdi, onZapri, izvozProgram }: {
  izvozProgram?: string | null;   // [PATCH sinhronizacija-program-ime v1]
  stevilo: number; izvajam: boolean; onPotrdi: () => void; onZapri: () => void;
}) {
  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={izvajam ? undefined : onZapri} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="pointer-events-auto w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="font-semibold text-gray-900 dark:text-white">Potrdi vse</h3>
            <button onClick={onZapri} disabled={izvajam}
              className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-40">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="px-5 py-4 space-y-2">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Potrdil boš <span className="font-semibold">{stevilo}</span>{' '}
              {stevilo === 1 ? 'postavko' : stevilo === 2 ? 'postavki' : 'postavk'}{' '}
              tipa »razhajanje identitete«.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {zaProgram('Pri vsaki se davčna in naziv prevzameta iz Opala.', izvozProgram)} Postavke tipa
              »dvojnik po davčni« in »verjetno združen« se NE dotaknejo.
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-400 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              Preveri posamezne primerjave, preden potrdiš vse naenkrat.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-800">
            <button onClick={onZapri} disabled={izvajam}
              className="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40">
              Prekliči
            </button>
            <button onClick={onPotrdi} disabled={izvajam}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
              {izvajam && <Loader className="w-4 h-4 animate-spin" />}
              Potrdi vse
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Stran                                                               */
/* ------------------------------------------------------------------ */
export function SinhronizacijaPage() {
  const { selectedCompany } = useAuthStore();
  const navigate = useNavigate();
  const [vse, setVse] = useState<Postavka[]>([]);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [nalagam, setNalagam] = useState(true);
  const [pogled, setPogled] = useState<'odprte' | 'zgodovina'>('odprte');
  const [razsirjene, setRazsirjene] = useState<number[]>([]);
  const [dialog, setDialog] = useState<{ postavka: Postavka; akcija: Akcija } | null>(null);
  const [vseDialog, setVseDialog] = useState(false);
  const [izvajam, setIzvajam] = useState(false);
  const [osveziVezavo, setOsveziVezavo] = useState(0);  // [PATCH multi-opal v1b]
  const [izvozProgram, setIzvozProgram] = useState<string | null>(null);   // [PATCH vasco-cakalnica v1]

  const nalozi = useCallback(() => {
    if (!selectedCompany) { setVse([]); setNalagam(false); return; }
    setNalagam(true);
    // prazen `status` -> streznik vrne VSE statuse (api/opal_cakalnica.py: `if status`)
    api.get('/profil/' + selectedCompany.id)   // [PATCH vasco-cakalnica v1] najprej program podjetja, nato prava cakalnica
      .then((p: any) => { const prog = p.data?.izvoz_program ?? null; setIzvozProgram(prog);
        return api.get(cakPot(prog) + selectedCompany.id, { params: { status: '' } }); })
      .then((r: any) => { setVse(r.data?.postavke || []); setNapaka(null); })
      .catch((e: any) => {
        // [PATCH opal-k8 v1] napako je treba ZADRZATI: brez tega bi se ob 403
        // ali 404 izrisalo prazno stanje "cakalnica je prazna", kar zavrnjenemu
        // uporabniku pove nekaj neresnicnega.
        setVse([]);
        setNapaka(e?.response?.data?.detail
          || 'Čakalnice ni bilo mogoče naložiti.');
      })
      .finally(() => setNalagam(false));
  }, [selectedCompany]);

  useEffect(nalozi, [nalozi]);

  const odprte = vse.filter((p) => p.status === 'odprta');
  const zgodovina = vse.filter((p) => p.status !== 'odprta');
  const prikazane = pogled === 'odprte' ? odprte : zgodovina;
  const odprtihIdentitet = odprte.filter((p) => p.tip === 'identiteta').length;

  const preklopi = (id: number) =>
    setRazsirjene((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  /* [PATCH opal-k8 d2] Razpri/strni VSE PRIKAZANE vrstice (par.2.2).
     Deluje samo nad trenutnim zavihkom — razsirjenost vrstic drugega
     zavihka ostane nedotaknjena, da preklop zavihka ne izgubi stanja. */
  const vseRazsirjene = prikazane.length > 0
    && prikazane.every((p) => razsirjene.includes(p.id));
  const preklopiVse = () =>
    setRazsirjene((s) => (vseRazsirjene
      ? s.filter((id) => !prikazane.some((p) => p.id === id))
      : Array.from(new Set([...s, ...prikazane.map((p) => p.id)]))));

  const izvedi = async (postavka: Postavka, akcija: Akcija, partnerId?: string) => {
    if (!selectedCompany) return;
    setIzvajam(true);
    try {
      const pot = `${cakPot(izvozProgram)}${selectedCompany.id}/${postavka.id}/${akcija}`;   // [PATCH vasco-cakalnica v1]
      const r = await api.post(pot, null,
        akcija === 'povezi' && partnerId ? { params: { partner_id: partnerId } } : undefined);
      const d = r.data || {};
      const opoz: string[] = d.opozorila || (d.opozorilo ? [d.opozorilo] : []);
      if (akcija === 'prestavi') {
        toast.success(`Šifra ${d.stara_sifra} → ${d.nova_sifra} (${d.nacin_stevilcenja || 'nova šifra'})`);
      } else if (akcija === 'povezi') {
        toast.success(`Partner je prevzel šifro ${d.nova_sifra}`);
      } else if (akcija === 'ustvari-loceno') {
        toast.success(`Ustvarjen ločen partner: ${d.naziv || postavka.sifra}`);
      } else {
        toast.success(`Postavka ${postavka.sifra} je razrešena`);
      }
      opoz.filter(Boolean).forEach((o) => toast(o, { icon: '⚠️', duration: 8000 }));
      setDialog(null);
      nalozi();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Akcije ni bilo mogoče izvesti');
    } finally {
      setIzvajam(false);
    }
  };

  const izvediVse = async () => {
    if (!selectedCompany) return;
    setIzvajam(true);
    try {
      const r = await api.post(`${cakPot(izvozProgram)}${selectedCompany.id}/potrdi-vse`);   // [PATCH vasco-cakalnica v1]
      const d = r.data || {};
      toast.success(`Potrjenih ${d.uspesnih} od ${d.obravnavanih}`);
      (d.napake || []).forEach((n: any) =>
        toast.error(`${n.sifra}: ${n.napaka}`, { duration: 10000 }));
      setVseDialog(false);
      nalozi();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Potrjevanja ni bilo mogoče izvesti');
    } finally {
      setIzvajam(false);
    }
  };

  if (!selectedCompany) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">Izberi podjetje v glavi strani.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Sinhronizacija</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {zaProgram('Čakalnica razhajanj med Opalom in AlfaBergom', izvozProgram)} — {selectedCompany.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pogled === 'odprte' && odprtihIdentitet > 1 && (
            <button onClick={() => setVseDialog(true)}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2">
              <Check className="w-4 h-4" />
              Potrdi vse ({odprtihIdentitet})
            </button>
          )}
          {prikazane.length > 0 && (
            <button onClick={preklopiVse}
              title={vseRazsirjene ? 'Strni vse vrstice' : 'Razpri vse vrstice'}
              className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400 flex items-center gap-2">
              {vseRazsirjene ? <ChevronsDownUp className="w-4 h-4" />
                : <ChevronsUpDown className="w-4 h-4" />}
              {vseRazsirjene ? 'Strni vse' : 'Razpri vse'}
            </button>
          )}
          <button onClick={() => { nalozi(); setOsveziVezavo((o) => o + 1); }} title="Osveži"  /* [PATCH multi-opal v1b] */
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <RefreshCw className={clsx('w-4 h-4', nalagam && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* [PATCH multi-opal v1] vezava podjetja na Opalovo bazo (naprava + baza) */}
      <VascoVezava companyId={selectedCompany.id} companyName={selectedCompany.name} osveziSignal={osveziVezavo} onSprememba={() => nalozi()} />   {/* [PATCH vasco-sync-fe v1] */}
      {izvozProgram !== 'vasco' && (   /* [PATCH sinhronizacija-program-ime v1] pri Vascu Opal vezava nima smisla */
        <OpalVezava companyId={selectedCompany.id} companyName={selectedCompany.name}
          osveziSignal={osveziVezavo} onSprememba={nalozi} />  /* [PATCH multi-opal v1b] */
      )}

      <Zavihki
        zavihki={[
          { id: 'odprte', label: `Odprte (${odprte.length})`, icon: AlertTriangle },
          { id: 'zgodovina', label: `Zgodovina (${zgodovina.length})`, icon: ScrollText },
        ]}
        aktiven={pogled}
        onIzberi={(id) => setPogled(id as 'odprte' | 'zgodovina')}
      />

      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        {nalagam ? (
          <div className="flex items-center justify-center h-40">
            <Loader className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : napaka ? (
          <div className="px-6 py-12 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <p className="text-gray-700 dark:text-gray-200">{napaka}</p>
            <button onClick={nalozi}
              className="mt-4 px-3 py-2 rounded-lg text-sm border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-400">
              Poskusi znova
            </button>
          </div>
        ) : prikazane.length === 0 ? (
          <div className="px-6 py-12 text-center">
            {pogled === 'odprte' ? (
              <>
                <Check className="w-10 h-10 text-green-500 mx-auto mb-3" />
                <p className="text-gray-700 dark:text-gray-200">
                  Čakalnica je prazna — sinhronizacija poteka brez razhajanj.
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  V zgodovini je {zgodovina.length}{' '}
                  {zgodovina.length === 1 ? 'razrešena postavka' : 'razrešenih postavk'}.
                </p>
              </>
            ) : (
              <p className="text-gray-400">Zgodovina je prazna.</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-3 py-3 w-8"></th>
                  <th className="px-3 py-3 text-left whitespace-nowrap">Šifra</th>
                  {/* [PATCH opal-k8 d2] naziv v zaprti vrstici */}
                  <th className="px-3 py-3 text-left">Partner</th>
                  <th className="px-3 py-3 text-left whitespace-nowrap">Tip</th>
                  <th className="px-3 py-3 text-left">Razlog</th>
                  {pogled === 'zgodovina' && (
                    <th className="px-3 py-3 text-left whitespace-nowrap">Status</th>
                  )}
                  <th className="px-3 py-3 text-left whitespace-nowrap">
                    {pogled === 'odprte' ? 'Nastala' : 'Razrešena'}
                  </th>
                  {pogled === 'odprte' && <th className="px-3 py-3"></th>}
                </tr>
              </thead>
              <tbody>
                {prikazane.map((p) => {
                  const razsirjena = razsirjene.includes(p.id);
                  const vrstice = primerjava(p.opal_podatki, p.alfaberg_podatki);
                  const kandidati = seznam(p.alfaberg_podatki?.kandidati);
                  return (
                    <React.Fragment key={p.id}>
                      <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-3 py-2">
                          <button onClick={() => preklopi(p.id)} title="Primerjava podatkov"
                            className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                            {razsirjena ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-3 py-2 font-mono font-medium text-gray-900 dark:text-white whitespace-nowrap">
                          {p.sifra}
                        </td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">
                          {nazivPostavke(p)
                            || <span className="text-gray-300 dark:text-gray-600">—</span>}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', TIP_BARVA[p.tip])}>
                            {TIP_OZNAKA[p.tip] || p.tip}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{p.opomba || ''}</td>
                        {pogled === 'zgodovina' && (
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            {STATUS_OZNAKA[p.status] || p.status}
                          </td>
                        )}
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {fmtCas(pogled === 'odprte' ? p.ustvarjeno : p.razreseno)}
                        </td>
                        {pogled === 'odprte' && (
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2 justify-end">
                              {p.tip === 'identiteta' && (
                                <>
                                  <button onClick={() => setDialog({ postavka: p, akcija: 'potrdi' })}
                                    title={zaProgram('Isti subjekt — veljajo Opalovi podatki', izvozProgram)}
                                    className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1.5 whitespace-nowrap">
                                    <Check className="w-3.5 h-3.5" />Potrdi
                                  </button>
                                  <button onClick={() => setDialog({ postavka: p, akcija: 'prestavi' })}
                                    title="Dva subjekta — AlfaBergov partner dobi novo šifro"
                                    className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400 flex items-center gap-1.5 whitespace-nowrap">
                                    <Split className="w-3.5 h-3.5" />Prestavi
                                  </button>
                                </>
                              )}
                              {p.tip === 'dvojnik_davcna' && (
                                <>
                                  <button onClick={() => setDialog({ postavka: p, akcija: 'povezi' })}
                                    title={zaProgram('Obstoječi partner prevzame Opalovo šifro', izvozProgram)}
                                    className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1.5 whitespace-nowrap">
                                    <Link2 className="w-3.5 h-3.5" />Poveži
                                    {kandidati.length > 1 ? ` (${kandidati.length})` : ''}
                                  </button>
                                  <button onClick={() => setDialog({ postavka: p, akcija: 'ustvari-loceno' })}
                                    title="Ustvari novega partnerja kljub isti davčni"
                                    className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400 flex items-center gap-1.5 whitespace-nowrap">
                                    <UserPlus className="w-3.5 h-3.5" />Ustvari ločeno
                                  </button>
                                </>
                              )}
                              {p.tip === 'info_zdruzitev' && (
                                <button onClick={() => setDialog({ postavka: p, akcija: 'potrdi' })}
                                  title="Označi kot videno in zapri"
                                  className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400 flex items-center gap-1.5 whitespace-nowrap">
                                  <X className="w-3.5 h-3.5" />Zapri
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>

                      {razsirjena && (
                        <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40">
                          <td colSpan={7} className="px-6 py-4">
                            {vrstice.length > 0 ? (
                              <table className="w-full text-sm">
                                <thead className="text-gray-500 dark:text-gray-400">
                                  <tr>
                                    <th className="py-1.5 text-left w-32">Polje</th>
                                    <th className="py-1.5 text-left">{zaProgram('Opal', izvozProgram)}</th>
                                    <th className="py-1.5 text-left">AlfaBerg</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {vrstice.map((v) => {
                                    const razlika = !enako(v.o, v.a);
                                    return (
                                      <tr key={v.polje} className={clsx(
                                        'border-t border-gray-200 dark:border-gray-700',
                                        razlika && 'bg-amber-50 dark:bg-amber-500/10')}>
                                        <td className="py-1.5 pr-3 text-gray-500 dark:text-gray-400">{v.polje}</td>
                                        <td className={clsx('py-1.5 pr-3', razlika
                                          ? 'font-medium text-amber-800 dark:text-amber-300'
                                          : 'text-gray-700 dark:text-gray-300')}>
                                          {prazno(v.o) ? <span className="text-gray-300 dark:text-gray-600">—</span> : String(v.o)}
                                        </td>
                                        <td className={clsx('py-1.5', razlika
                                          ? 'font-medium text-amber-800 dark:text-amber-300'
                                          : 'text-gray-700 dark:text-gray-300')}>
                                          {prazno(v.a) ? <span className="text-gray-300 dark:text-gray-600">—</span> : String(v.a)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            ) : (
                              <p className="text-sm text-gray-400">Ni polj za primerjavo.</p>
                            )}

                            {vrstice.some((v) => POLJA_PRETVORBA.includes(v.polje)
                              && !enako(v.o, v.a)) && (
                              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                {zaProgram('Opalove vrednosti so prikazane surovo.', izvozProgram)} Kraj, pošto in
                                državo sinhronizacija ob prepisu pretvori (npr.
                                »1000&nbsp;&nbsp;Ljubljana« → kraj + pošta,
                                »Slovenija« → SI), zato razlika v teh vrsticah ne
                                pomeni nujno, da se bo vrednost spremenila.
                              </p>
                            )}

                            {kandidati.length > 0 && (
                              <div className="mt-4">
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                  Kandidati v AlfaBergu ({p.alfaberg_podatki?.kandidatov_skupaj ?? kandidati.length}):
                                </p>
                                <ul className="space-y-1">
                                  {kandidati.map((k: any, i: number) => (
                                    <li key={i} className="text-sm text-gray-600 dark:text-gray-300">
                                      {typeof k === 'string' ? (
                                        <>šifra <span className="font-mono">{k}</span></>
                                      ) : (
                                        <>
                                          <span className="font-medium text-gray-900 dark:text-white">{k.name}</span>
                                          {' · šifra '}<span className="font-mono">{k.vasco_code || '—'}</span>
                                          {' · davčna '}
                                          <span className="font-mono">
                                            {[k.tax_number, k.vat_number].filter((x: any) => !prazno(x)).join(' / ') || '—'}
                                          </span>
                                        </>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {p.status !== 'odprta' && (
                              <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                                Razrešil: <span className="font-mono">{p.razresil_user_id || '—'}</span>
                                {' · '}{fmtCas(p.razreseno)}
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* par.2.4 povezava na Dnevnik. DnevnikPage drzi filter `vrsta` v
          lokalnem stanju (ne v URL), zato predizbranega filtra ni mogoce
          podati po povezavi — Dnevnika po navodilu ne spreminjamo. */}
      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
        <ScrollText className="w-4 h-4" />
        Vse spremembe sinhronizacije so zabeležene v{' '}
        <button onClick={() => navigate('/dnevnik')}
          className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
          Dnevniku
        </button>
      </p>

      {dialog && (
        <PotrditevDialog izvozProgram={izvozProgram}
          postavka={dialog.postavka}
          akcija={dialog.akcija}
          izvajam={izvajam}
          onPotrdi={(partnerId) => izvedi(dialog.postavka, dialog.akcija, partnerId)}
          onZapri={() => { if (!izvajam) setDialog(null); }}
        />
      )}
      {vseDialog && (
        <PotrdiVseDialog izvozProgram={izvozProgram}
          stevilo={odprtihIdentitet}
          izvajam={izvajam}
          onPotrdi={izvediVse}
          onZapri={() => { if (!izvajam) setVseDialog(false); }}
        />
      )}
    </div>
  );
}
